import { CSSProperties, useId } from "react";
import styled, { css, DefaultTheme } from "styled-components";
import { gsap } from "gsap";
import { lengthFractions, type Pt } from "features/onboarding/art/chart";

/*
 * Animated drawing kit for the onboarding illustrations, ported from the
 * cornix.io redesign (components/redesign/feature/art/live.tsx).
 *
 * The style: plain SVG, UI cards with a hairline border and a soft shadow, the
 * brand-blue price line, green BUY and red SELL chips, small labels, floating
 * toasts. The markup is the scene at rest, fully drawn: that is what shows with
 * reduced motion and in e2e runs. Elements the scene animates carry `data-k`;
 * card frames carry `data-in` and fade in once. A scene only loops while its
 * illustration is on screen.
 */

const E2E_STATIC = import.meta.env.VITE_E2E_DISABLE_ANT_MOTION === "true";

export const motionAllowed = (): boolean =>
  typeof window !== "undefined" &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export type Tone = "up" | "down" | "accent" | "ink" | "gold";

const STROKE: Record<Tone, string> = {
  up: "var(--viz-up)",
  down: "var(--viz-down)",
  accent: "var(--viz-price)",
  ink: "var(--viz-ink)",
  gold: "var(--viz-gold)",
};
const SOFT: Record<Tone, string> = {
  up: "var(--viz-up-soft)",
  down: "var(--viz-down-soft)",
  accent: "var(--viz-accent-soft)",
  ink: "var(--viz-ink-soft)",
  gold: "var(--viz-gold-soft)",
};

export const tone = (t: Tone): string => STROKE[t];
export const soft = (t: Tone): string => SOFT[t];

export type Box = { x: number; y: number; w: number; h: number };

/** Text classes (see `ArtRoot`) and a font helper for inline sizes. */
export const TXT = {
  label: "ak-label",
  caps: "ak-caps",
  ink: "ak-ink",
  ink2: "ak-ink2",
  ink3: "ak-ink3",
  num: "ak-num",
  halo: "ak-halo",
};
export const font = (size: number, weight = 400): CSSProperties => ({
  fontSize: size,
  fontWeight: weight,
});

/** Height of a `Chip`; `sm` is the lighter one every thumbnail uses. */
export const chipHeight = (sm?: boolean): number => (sm ? 20 : 24);

/**
 * Where a chip sits in a card's top-right corner: the same inset from the top
 * and the right edge (clear of the corner radius).
 */
export const cornerChip = (card: Box, w: number, sm?: boolean, inset = 8) => ({
  x: card.x + card.w - inset - w / 2,
  y: card.y + inset + chipHeight(sm) / 2,
});

/** Rounds to 0.01 so repeated renders produce identical attributes. */
export const r2 = (n: number): number => Math.round(n * 100) / 100;

/** Maps unit points (0–1 across, 0–1 down) into a box. */
export const place = (b: Box, pts: Pt[]): Pt[] =>
  pts.map(([u, v]) => [b.x + u * b.w, b.y + v * b.h]);

/** Position along a polyline by length fraction, for heads that ride the price line. */
export function follow(pts: Pt[]) {
  const { fractions } = lengthFractions(pts);
  const at = (p: number): Pt => {
    const f = Math.min(1, Math.max(0, p));
    let i = 1;
    while (i < fractions.length - 1 && fractions[i] < f) i++;
    const span = fractions[i] - fractions[i - 1] || 1;
    const k = (f - fractions[i - 1]) / span;
    return [
      pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * k,
      pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k,
    ];
  };
  return { at, fractions };
}

/**
 * Where a polyline first reaches level `y` moving `down` in price (SVG y
 * growing) or `up`, after point `from`.
 */
export function reach(pts: Pt[], y: number, dir: "down" | "up", from = 0) {
  const { fractions } = lengthFractions(pts);
  for (let i = Math.max(1, from + 1); i < pts.length; i++) {
    const [ax, ay] = pts[i - 1];
    const [bx, by] = pts[i];
    if (dir === "down" ? ay < y && by >= y : ay > y && by <= y) {
      const t = (y - ay) / (by - ay);
      return {
        x: r2(ax + (bx - ax) * t),
        y,
        f: fractions[i - 1] + (fractions[i] - fractions[i - 1]) * t,
        i,
      };
    }
  }
  return null;
}

/* ------------------------------------------------------------------------ */
/* Palette                                                                  */
/* ------------------------------------------------------------------------ */

// Illustration palette. Blues and text come from the theme; the trading
// colors follow the cornix.io illustrations (calmer than the UI's PnL green).
export const artPalette = (theme: DefaultTheme) => {
  const c = theme.colors;
  const dark = theme.isDarkMode;
  return css`
    --viz-price: ${c.primaryBlue};
    --viz-accent: ${dark ? "#6f9dff" : "#5890f9"};
    --viz-accent-soft: ${dark
      ? "rgba(111, 157, 255, 0.2)"
      : "rgba(88, 144, 249, 0.16)"};
    --viz-accent-faint: ${dark
      ? "rgba(111, 157, 255, 0.06)"
      : "rgba(88, 144, 249, 0.06)"};
    --viz-accent-line: ${dark
      ? "rgba(111, 157, 255, 0.3)"
      : "rgba(88, 144, 249, 0.35)"};
    --viz-up: ${dark ? "#3ee09a" : "#1a9e66"};
    --viz-up-soft: ${dark
      ? "rgba(62, 224, 154, 0.14)"
      : "rgba(26, 158, 102, 0.14)"};
    --viz-down: ${dark ? "#ff7a8a" : "#e0415b"};
    --viz-down-soft: ${dark
      ? "rgba(255, 122, 138, 0.14)"
      : "rgba(224, 65, 91, 0.12)"};
    --viz-gold: ${dark ? "#f5c451" : "#d39410"};
    --viz-gold-soft: ${dark
      ? "rgba(245, 196, 81, 0.16)"
      : "rgba(211, 148, 16, 0.14)"};
    --viz-rule: ${dark
      ? "rgba(255, 255, 255, 0.12)"
      : "rgba(13, 20, 78, 0.12)"};
    --viz-rule-soft: ${dark
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(13, 20, 78, 0.06)"};
    --viz-ink: ${c.titleColor};
    --viz-ink-2: ${c.mediumText};
    --viz-ink-3: ${c.secondaryText};
    --viz-ink-soft: ${dark
      ? "rgba(255, 255, 255, 0.07)"
      : "rgba(13, 20, 78, 0.06)"};
    --viz-card: ${c.cardBgColor};
    --viz-card-line: ${dark
      ? "rgba(255, 255, 255, 0.09)"
      : "rgba(13, 20, 78, 0.08)"};
    --viz-on-solid: ${dark ? "#0d144e" : "#ffffff"};
    --viz-shadow: ${dark ? "#000000" : "#0d144e"};
  `;
};

const artBob = `
  @keyframes onboarding-art-bob {
    50% {
      transform: translateY(-8px);
    }
  }
  @keyframes onboarding-art-pulse {
    0% {
      transform: scale(0.6);
      opacity: 0.9;
    }
    100% {
      transform: scale(2.4);
      opacity: 0;
    }
  }
  @keyframes onboarding-art-rise {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
  }
  @keyframes onboarding-art-pop {
    from {
      opacity: 0;
      transform: scale(0.2);
    }
  }
`;

export const ArtRoot = styled.div`
  ${(p) => artPalette(p.theme)};
  width: 100%;
  line-height: 0;
  svg.ak-canvas {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    font-family: ${(p) => p.theme.typography.fontFamily};
  }
  svg.ak-fill {
    height: 100%;
  }
  text {
    font-family: inherit;
  }
  .ak-ink {
    fill: var(--viz-ink);
  }
  .ak-ink2 {
    fill: var(--viz-ink-2);
  }
  .ak-ink3 {
    fill: var(--viz-ink-3);
  }
  .ak-num {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
  .ak-label {
    fill: var(--viz-ink-3);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-variant-numeric: tabular-nums;
  }
  .ak-caps {
    fill: var(--viz-ink-3);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  /* A card-colored outline, so labels stay readable where lines cross them. */
  .ak-halo {
    paint-order: stroke;
    stroke: var(--viz-card);
    stroke-width: 4px;
    stroke-linejoin: round;
  }
  ${E2E_STATIC
    ? ""
    : css`
        ${artBob}
        @media (prefers-reduced-motion: no-preference) {
          /* Step scenes: elements that appear with an event. */
          .ak-rise {
            animation: onboarding-art-rise 0.6s cubic-bezier(0.16, 1, 0.3, 1)
              var(--ak-delay, 0s) both;
          }
          .ak-pop {
            transform-box: fill-box;
            transform-origin: center;
            animation: onboarding-art-pop 0.45s
              cubic-bezier(0.34, 1.56, 0.64, 1) var(--ak-delay, 0s) both;
          }
          .ak-float {
            animation: onboarding-art-bob 6s ease-in-out infinite;
          }
          /* Step scenes: a level that moves with an event glides there. */
          .ak-glide {
            transition: transform 0.7s cubic-bezier(0.45, 0, 0.55, 1)
              var(--ak-delay, 0s);
          }
          .ak-pulse {
            transform-box: fill-box;
            transform-origin: center;
            animation: onboarding-art-pulse 2.6s cubic-bezier(0.16, 1, 0.3, 1)
              infinite;
          }
          svg[data-on="false"] .ak-float,
          svg[data-on="false"] .ak-pulse {
            animation-play-state: paused;
          }
        }
      `}
`;

/* ------------------------------------------------------------------------ */
/* Scene runtime                                                            */
/* ------------------------------------------------------------------------ */

export type Pick = (k: string) => Element[];

/**
 * Builds the loop on `tl` (it repeats forever) and returns the time at which
 * the scene is at rest, matching the markup. `$('name')` picks
 * `[data-k="name"]`, and `$('name*')` every element whose key starts with it.
 */
export type Scene = (tl: gsap.core.Timeline, $: Pick) => number;

/** A stable id for filter/gradient references inside one illustration. */
export const useArtId = (name: string): string =>
  `${name}-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

/* ------------------------------------------------------------------------ */
/* Timeline helpers                                                         */
/* ------------------------------------------------------------------------ */

type Targets = Element | Element[];

/** Draws a `Line` (pathLength 1) from its start, by attribute. */
export function draw(
  tl: gsap.core.Timeline,
  el: Targets,
  at: number,
  dur: number,
  ease = "none",
  from = 1,
  to = 0
): void {
  tl.fromTo(
    el,
    { attr: { "stroke-dashoffset": from } },
    { attr: { "stroke-dashoffset": to }, duration: dur, ease },
    at
  );
}

/** Pops a `Marker` (or any circle) open by radius, so it stays centered. */
export function pop(
  tl: gsap.core.Timeline,
  el: Targets,
  at: number,
  r = 6.5
): void {
  tl.fromTo(
    el,
    { attr: { r: 0 }, opacity: 0 },
    { attr: { r }, opacity: 1, duration: 0.5, ease: "back.out(2.6)" },
    at
  );
}

/** Expanding ring from a point. Rings rest invisible (opacity 0 in the markup). */
export function ring(
  tl: gsap.core.Timeline,
  el: Targets,
  at: number,
  r0 = 6,
  r1 = 24
): void {
  tl.fromTo(
    el,
    { attr: { r: r0 }, opacity: 0.9 },
    {
      attr: { r: r1 },
      opacity: 0,
      duration: 0.9,
      ease: "expo.out",
      immediateRender: false,
    },
    at
  );
}

/** Fades an element in with a short rise. */
export function rise(
  tl: gsap.core.Timeline,
  el: Targets,
  at: number,
  dy = 8,
  dur = 0.6
): void {
  tl.fromTo(
    el,
    { opacity: 0, y: dy },
    { opacity: 1, y: 0, duration: dur, ease: "expo.out" },
    at
  );
}

/** Fades elements out (the clear at the end of a loop). */
export function fade(
  tl: gsap.core.Timeline,
  el: Targets,
  at: number,
  dur = 0.5
): void {
  tl.to(el, { opacity: 0, duration: dur, ease: "power1.in" }, at);
}

/** Counts a text element from one value to another. */
export function count(
  tl: gsap.core.Timeline,
  el: Element | undefined,
  from: number,
  to: number,
  fmt: (v: number) => string,
  at: number,
  dur = 1,
  reset = true
): void {
  if (!el) return;
  const o = { v: from };
  if (reset) tl.call(() => void (el.textContent = fmt(from)), [], 0);
  tl.fromTo(
    o,
    { v: from },
    {
      v: to,
      duration: dur,
      ease: "power2.out",
      onUpdate: () => void (el.textContent = fmt(o.v)),
    },
    at
  );
}

/** Sets a text element at a point in the loop; with `first`, also at its start. */
export function say(
  tl: gsap.core.Timeline,
  el: Element | undefined,
  text: string,
  at: number,
  first?: string
): void {
  if (!el) return;
  if (first !== undefined) tl.call(() => void (el.textContent = first), [], 0);
  tl.call(() => void (el.textContent = text), [], at);
}

/** Rides a head group along a polyline while its `Line` draws. */
export function ride(
  tl: gsap.core.Timeline,
  line: Targets,
  head: Element | undefined,
  pts: Pt[],
  at: number,
  dur: number,
  from = 0,
  to = 1
) {
  const path = follow(pts);
  const stroke = (Array.isArray(line) ? line[0] : line) as
    | SVGPathElement
    | undefined;
  const length = stroke?.getTotalLength() ?? 0;
  const o = { p: from };
  const move = () => {
    const point = stroke?.getPointAtLength(o.p * length);
    const [x, y] = point ? [point.x, point.y] : path.at(o.p);
    head?.setAttribute(
      "transform",
      `translate(${x.toFixed(3)} ${y.toFixed(3)})`
    );
  };
  draw(tl, line, at, dur, "none", 1 - from, 1 - to);
  tl.fromTo(
    o,
    { p: from },
    { p: to, duration: dur, ease: "none", onUpdate: move, onStart: move },
    at
  );
  if (head && !from)
    tl.fromTo(head, { opacity: 0 }, { opacity: 1, duration: 0.3 }, at);
  return path;
}
